import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { buildAbsoluteUrl, derivePageMeta } from "../../utils/seo";
import SeoAlternateLinks from "../../components/seo/SeoAlternateLinks";
import HeroHeading from "../../components/ui/HeroHeading";
import FaqSection from "../../components/FaqSection";
import LazyVideo from "../../components/LazyVideo";
import { colors } from "../../styles/colors";
import { useResponsive } from "../../hooks/useResponsive";
import { useContent } from "../../cms/ContentContext";

const FaqPage = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();

  const meta = derivePageMeta({
    t,
    i18n,
    pageKey: "faq",
    routeKey: "faq",
    defaults: {
      title: "Cold Experience FAQ",
      description: t(
        "faq.meta.description",
        "Svar på vanliga frågor om resan till Lappland, vad du ska packa, försäkringar och vad som ingår."
      ),
      keywords:
        "Cold Experience FAQ, Lappland frågor, snöskoterresa frågor, vad ska jag packa Lappland",
      ogImage: "/og/faq.jpg",
    },
  });

  const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
  const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);
  const heroSpacing = isMobile ? "px-4 pt-24 pb-32" : "px-4 sm:px-6 lg:px-8 pt-32 pb-40";
  const heroMaxWidth = isMobile ? "max-w-sm" : "max-w-4xl";
  const heroVideo = getMedia('faq.hero.videoSrc', '/optimized_videos/coldexperience1-opt.mp4');
  const heroPoster = getMedia('faq.hero.poster', '/images/Nya_bilder/IMG_0451.webp');

  return (
    <>
      <Helmet>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        <link rel="canonical" href={meta.canonicalUrl} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta name="twitter:image" content={twitterImageUrl} />
        {meta.jsonLd && (
          <script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
        )}
      </Helmet>
      <SeoAlternateLinks
        routeKey="faq"
        canonicalPath={meta.canonicalPath}
        siteUrl={meta.siteUrl}
      />
      <div className="min-h-screen" style={{ background: colors.gradients.heroSolid }}>
        <section data-cms-section="faq:hero" data-cms-page="contact" className="relative min-h-screen overflow-hidden flex items-center">
          <div className="absolute inset-0">
            <LazyVideo
              src={heroVideo}
              poster={heroPoster}
              autoPlay
              muted
              loop
              playsInline
              preload={isMobile ? "none" : "metadata"}
              className="absolute inset-0"
              videoClassName="absolute inset-0 w-full h-full object-cover"
              pauseWhenOutOfView
            />
            <div
              className="absolute inset-0 opacity-80"
              style={{ background: colors.gradients.hero }}
            ></div>
          </div>
          <div className={`relative z-10 w-full ${heroSpacing}`}>
            <div className={`${heroMaxWidth} mx-auto text-center`}>
              <HeroHeading
                primary={t("faq.hero.title1", "Allt du behöver veta")}
                accent={t("faq.hero.title2", "inför Lappland")}
                className={`mb-6 ${isMobile ? "text-4xl" : "text-5xl md:text-6xl"}`}
              />
              <p
                className={`${isMobile ? "text-lg max-w-md" : "text-xl max-w-3xl"} mx-auto`}
                style={{ color: colors.text.secondary }}
              >
                {t(
                  "faq.hero.subtitle",
                  "Vanliga frågor om resvägar, packning, försäkringar och hur allt fungerar hos oss."
                )}
              </p>
            </div>
          </div>
        </section>
        <FaqSection />
      </div>
    </>
  );
};

export default FaqPage;
